/* eslint-disable complexity */
import { EMPTY_STRING } from '@/data/constants/marketing';

export enum DP_FAN_STAGES {
	INITITAL = 'INITITAL',
	SPORTS = 'SPORTS',
	TEAMS = 'TEAMS',
	SIZES = 'SIZES',
	DAY_BY_DAY = 'DAY_BY_DAY',
	FINISH = 'FINISH',
}

export type DpFanStagesStateTeams = string[];
export type DpFanStagesStateSizes = Record<string, string>;
export type DpFanStagesStateSports = string[];
export type DpFanStagesStateDayByDay = string[];

export type DpFanStagesState = {
	stage: DP_FAN_STAGES;
	stepIndex: number;
	activeCategory: string; // The specific sport being configured in TEAMS stage
	sports: DpFanStagesStateSports;
	teams: DpFanStagesStateTeams;
	size: DpFanStagesStateSizes;
	dayByDay: DpFanStagesStateDayByDay;
	submitting: boolean;
	needToSync: boolean;
};

export const initialDpFanStages: DpFanStagesState = {
	stage: DP_FAN_STAGES.INITITAL,
	stepIndex: 0,
	activeCategory: '',
	sports: [],
	teams: [],
	dayByDay: [],
	size: {},
	submitting: false,
	needToSync: true,
};

export enum DP_FAN_STAGES_ACTIONS {
	START_SUBMIT = 'START_SUBMIT',
	CHANGE_SPORT = 'CHANGE_SPORT',
	CHANGE_TEAM = 'CHANGE_TEAM',
	CHANGE_SIZE = 'CHANGE_SIZE',
	CHANGE_DAY_BY_DAY = 'CHANGE_DAY_BY_DAY',
	END_SUBMIT = 'END_SUBMIT',
	PREV_STAGE = 'PREV_STAGE',
	NEXT_STAGE = 'NEXT_STAGE',
	PARTIAL_UPDATE = 'PARTIAL_UPDATE',
}

type DpFanStagesAction =
	| { type: DP_FAN_STAGES_ACTIONS.START_SUBMIT }
	| { type: DP_FAN_STAGES_ACTIONS.END_SUBMIT }
	| { type: DP_FAN_STAGES_ACTIONS.CHANGE_SPORT; sport: string }
	| { type: DP_FAN_STAGES_ACTIONS.CHANGE_TEAM; team: string }
	| { type: DP_FAN_STAGES_ACTIONS.CHANGE_DAY_BY_DAY; dayByDay: string }
	| { type: DP_FAN_STAGES_ACTIONS.CHANGE_SIZE; category: string; size: string }
	| {
			type: DP_FAN_STAGES_ACTIONS.PREV_STAGE;
			updates?: Partial<DpFanStagesState>;
	  }
	| {
			type: DP_FAN_STAGES_ACTIONS.NEXT_STAGE;
			updates?: Partial<DpFanStagesState>;
	  }
	| { type: DP_FAN_STAGES_ACTIONS.PARTIAL_UPDATE; updates: Partial<DpFanStagesState> };

// Helper to calculate the sequence of steps based on selected sports
const getSteps = () => {
	const steps: { stage: DP_FAN_STAGES; category: string }[] = [
		{ stage: DP_FAN_STAGES.INITITAL, category: '' },
		{ stage: DP_FAN_STAGES.SPORTS, category: '' },
		{ stage: DP_FAN_STAGES.DAY_BY_DAY, category: '' },
		{ stage: DP_FAN_STAGES.TEAMS, category: '' },
		{ stage: DP_FAN_STAGES.SIZES, category: '' },
		{ stage: DP_FAN_STAGES.FINISH, category: '' },
	];

	return steps;
};

export const dpFanStagesReducer = (
	state: DpFanStagesState,
	action: DpFanStagesAction
): DpFanStagesState => {
	switch (action.type) {
		case DP_FAN_STAGES_ACTIONS.START_SUBMIT:
			return { ...state, submitting: true };
		case DP_FAN_STAGES_ACTIONS.END_SUBMIT:
			return { ...state, submitting: false };
		case DP_FAN_STAGES_ACTIONS.PREV_STAGE: {
			const steps = getSteps();
			const newIndex = Math.max(0, state.stepIndex - 1);
			const prevStep = steps[newIndex];
			return {
				...state,
				stepIndex: newIndex,
				stage: prevStep.stage,
				activeCategory: prevStep.category,
				...action.updates,
			};
		}
		case DP_FAN_STAGES_ACTIONS.NEXT_STAGE: {
			const steps = getSteps();
			const newIndex = Math.min(steps.length - 1, state.stepIndex + 1);
			const nextStep = steps[newIndex];
			return {
				...state,
				stepIndex: newIndex,
				stage: nextStep.stage,
				activeCategory: nextStep.category,
				...action.updates,
			};
		}
		case DP_FAN_STAGES_ACTIONS.CHANGE_SPORT: {
			const isExist = state.sports.includes(action.sport);
			const newSports = isExist
				? state.sports.filter((sport) => sport !== action.sport)
				: [...state.sports, action.sport];

			return { ...state, sports: newSports };
		}
		case DP_FAN_STAGES_ACTIONS.CHANGE_TEAM: {
			const isExist = state.teams.includes(action.team);
			const newTeams = isExist
				? state.teams.filter((team) => team !== action.team)
				: [...state.teams, action.team];

			return {
				...state,
				teams: newTeams,
			};
		}
		case DP_FAN_STAGES_ACTIONS.CHANGE_DAY_BY_DAY: {
			const isExist = state.dayByDay.includes(action.dayByDay);
			const newDayByDay = isExist
				? state.dayByDay.filter((dayByDay) => dayByDay !== action.dayByDay)
				: [...state.dayByDay, action.dayByDay];

			return {
				...state,
				dayByDay: newDayByDay,
			};
		}
		case DP_FAN_STAGES_ACTIONS.CHANGE_SIZE: {
			const isDeleted = action.size === EMPTY_STRING;
			const newSizes = { ...state.size };

			if (isDeleted) {
				delete newSizes[action.category];
			} else {
				newSizes[action.category] = action.size;
			}

			return { ...state, size: newSizes };
		}
		case DP_FAN_STAGES_ACTIONS.PARTIAL_UPDATE:
			return { ...state, ...action.updates };
		default:
			return state;
	}
};
